/**
 * InterviewerAvatar — Friendly AI Robot
 * Pure CSS <div> avatar — no SVG, no photos.
 * Styled to match the white rounded-body robot with dark visor and cyan eyes.
 *
 * Status:
 *   speaking  → mouth opens (RAF oscillator) + eyes glow bright + wave bars
 *   listening → green ring pulse around visor
 *   loading   → eyes dim-pulse
 *   idle      → gentle float animation
 */

import React, { useEffect, useRef, useState } from 'react';
import './InterviewerAvatar.css';

const SPEAK_LEVELS  = [0, 1, 2, 3, 2, 1, 0, 1, 2, 1, 3, 2, 1, 0];
const SMILE_H_PX    = [5, 9, 14, 20]; // mouth height per speak level

export default function InterviewerAvatar({ status }) {
  const [speakLevel, setSpeakLevel] = useState(0);
  const [blink,      setBlink]      = useState(false);
  const frameRef = useRef(null);

  /* ── RAF mouth oscillator ── */
  useEffect(() => {
    if (status !== 'speaking') { setSpeakLevel(0); return; }
    let idx = 0, lastTs = 0;
    const nextMs = () => 85 + Math.random() * 120;
    let wait = nextMs();
    const tick = (ts) => {
      if (ts - lastTs >= wait) {
        idx = (idx + 1) % SPEAK_LEVELS.length;
        setSpeakLevel(SPEAK_LEVELS[idx]);
        lastTs = ts; wait = nextMs();
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [status]);

  /* ── Blink every 3-6 s ── */
  useEffect(() => {
    let t;
    const schedule = () => {
      t = setTimeout(() => {
        setBlink(true);
        setTimeout(() => setBlink(false), 120);
        schedule();
      }, 3000 + Math.random() * 3500);
    };
    schedule();
    return () => clearTimeout(t);
  }, []);

  const isSpeaking  = status === 'speaking';
  const isListening = status === 'listening';
  const isLoading   = status === 'loading';
  const smileH      = SMILE_H_PX[speakLevel] ?? 5;

  return (
    <div className={[
      'rb-frame',
      isSpeaking  ? 'rb-speaking'  : '',
      isListening ? 'rb-listening' : '',
      isLoading   ? 'rb-loading'   : '',
    ].join(' ')}>

      {/* Floating inner */}
      <div className={`rb-inner ${isSpeaking ? 'rb-inner-paused' : ''}`}>

        {/* ── Arms (behind body) ── */}
        <div className="rb-arm rb-arm-l" />
        <div className="rb-arm rb-arm-r" />

        {/* ── Body ── */}
        <div className="rb-body">
          <div className="rb-body-shine" />
          <div className="rb-body-panel" />
        </div>

        {/* ── Neck ── */}
        <div className="rb-neck" />

        {/* ── Head (white helmet) ── */}
        <div className="rb-head">
          <div className="rb-head-shine" />

          {/* Antenna nub */}
          <div className="rb-antenna" />

          {/* Dark visor / screen */}
          <div className="rb-visor">
            <div className="rb-visor-glow" />

            {/* Eyes */}
            <div className={`rb-eye rb-eye-l ${blink ? 'rb-blink' : ''} ${isSpeaking ? 'rb-eye-bright' : ''}`}>
              <div className="rb-eye-inner" />
              <div className="rb-eye-glint" />
            </div>
            <div className={`rb-eye rb-eye-r ${blink ? 'rb-blink' : ''} ${isSpeaking ? 'rb-eye-bright' : ''}`}>
              <div className="rb-eye-inner" />
              <div className="rb-eye-glint" />
            </div>

            {/* Mouth / smile */}
            <div className="rb-smile-wrap">
              <div className="rb-smile" style={{ height: `${smileH}px` }}>
                {smileH > 9 && <div className="rb-smile-gap" />}
              </div>
            </div>

            {/* Listening ring overlay */}
            {isListening && <div className="rb-listen-overlay" />}
          </div>
        </div>

        {/* ── Drop shadow ── */}
        <div className="rb-shadow" />
      </div>

      {/* ── Speaking wave bars (outside frame, below) ── */}
      {isSpeaking && (
        <div className="rb-waves">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="rb-wave-bar"
              style={{ animationDelay: `${i * 0.11}s` }} />
          ))}
        </div>
      )}
    </div>
  );
}
